import React from 'react';
import { StepperChangeEvent, StepProps } from '@progress/kendo-react-layout';
import {
  enums,
  get,
  parseJSONOrDefault,
  trimPathEndSlash,
} from '../../../utils';
import { models } from '../../../utils/cms-client';
import { getFormConfig } from '../../../utils/remote-config-manager';
import { Case, PersonData } from '../../../data-models';
import { log } from '../../../utils/log';
import { ComponentProps } from '.';
import { IFormContext } from '../../../contexts/form-context';
import * as options from '../options';
import { FormSubmitClickEvent } from '@progress/kendo-react-form';
import { findFormStepIndex } from './handlers';

// const log = new Logger\('forms/form/utils');

export interface CustomStepProps extends StepProps {
  id: string;
  // Kept for backward compatibility with tests and legacy callers.
  formStepId?: string;
}

export const transformFormStepsForStepper = (
  steps: models.FormStep[],
): CustomStepProps[] =>
  steps.map((step) => {
    return {
      label: step.elements.label?.value ?? '',
      id: step.system.id,
    };
  });

export const isPreviousStepsValid = (
  steps: CustomStepProps[],
  currentStepIndex: number,
) =>
  steps
    .slice(0, currentStepIndex)
    .findIndex((currentStep) => currentStep.isValid === false) === -1;

export const findStepIndexWithLeavePlanGraph = (
  form: models.Form | undefined,
) => {
  // find the first step with a leave plan graph
  return (
    form?.elements.steps?.linkedItems?.findIndex((fs) => {
      const fieldsOnly = fs.elements.sections?.linkedItems?.filter(
        (s) => s.system.type === enums.FormSectionTypes.FormField,
      );

      return fieldsOnly?.some(
        (s) =>
          (s as any).elements?.form_field_types?.value[0]?.name ===
          enums.FormFieldTypes.leaveplangraph,
      );
    }) ?? -1
  );
};

export const setConfig = async (
  lastPath: string,
  setNotFound: React.Dispatch<React.SetStateAction<boolean>>,
  setForm: React.Dispatch<React.SetStateAction<models.Form | undefined>>,
  setFormSteps: React.Dispatch<React.SetStateAction<models.FormStep[]>>,
  form: models.Form | undefined,
  setFormContext: React.Dispatch<React.SetStateAction<any>>,
) => {
  const formResult = form || (await getFormConfig(lastPath))?.items?.[0];

  if (!formResult) {
    // we weren't passed a form, and we didn't find a form, show 404
    log.error('setConfig', lastPath, form);
    setNotFound(true);
    return;
  }

  setForm(formResult);

  if (formResult?.elements.steps?.linkedItems?.length > 0) {
    const stepIndexWithLeavePlanGraph =
      findStepIndexWithLeavePlanGraph(formResult);

    // set it on the formContext
    if (stepIndexWithLeavePlanGraph > -1) {
      setFormContext((c: any) =>
        Object.assign(c || {}, { stepIndexWithLeavePlanGraph }),
      );
    }
  }

  if (formResult?.system?.name) {
    setFormContext((c: any) =>
      Object.assign(c || {}, { formName: formResult.system.name }),
    );
  }

  const formConfigSteps = formResult?.elements.steps?.linkedItems || [];
  setFormSteps(formConfigSteps);
};

export const determineIntegrated = (pd: PersonData) => {
  const isInOtherList = (parentId: string, list: Case[]) =>
    list?.some((c) => c.parentId === parentId) || false;

  for (const c of pd.cases?.claims || []) {
    c.integrated = isInOtherList(
      c.parentId as string,
      pd.cases?.leaves as Case[],
    );
  }

  for (const l of pd.cases?.leaves || []) {
    l.integrated = isInOtherList(
      l.parentId as string,
      pd.cases?.claims as Case[],
    );
  }

  return pd;
};

export interface IMoveToStepParams {
  allowNavBack: boolean;
  currentStepHasLeavePlanGraph: any;
  currentStepIndex: number;
  formContext: any;
  setCurrentStepIndex: Function;
  setDisplayConfirmationModal: Function;
  setFormStepIndex: Function;
  setStepGoBack: Function;
  stepperChangeEvent?: StepperChangeEvent;
  stepCodename?: string;
  steps: CustomStepProps[];
  formSteps: models.FormStep[] | undefined;
}

export const moveToStep = (p: IMoveToStepParams) => {
  const nsi =
    p.stepCodename === undefined
      ? p.stepperChangeEvent?.value
      : resolveStepIndexByCodename(p.formSteps ?? [], p.stepCodename);

  if (nsi === undefined || nsi < 0) return;

  if (nsi < p.currentStepIndex && p.allowNavBack) {
    if (
      p.formContext?.currentLeavePlan?.planId &&
      p.currentStepHasLeavePlanGraph &&
      p.formContext?.stepIndexWithLeavePlanGraph > nsi
    ) {
      p.setStepGoBack(nsi);
      p.setDisplayConfirmationModal(true);
    } else {
      p.setCurrentStepIndex(nsi);
      p.setFormStepIndex(findFormStepIndex(p.steps[nsi], p.formSteps || []));
    }
  }
};

export const processNavigationObject = (p: ComponentProps) => {
  const path = trimPathEndSlash(get(p, 'location.pathname', '/not-found'));
  const pathParts = path.split('/');
  const lastPath = pathParts[pathParts.length - 1];
  const task = get(p, 'location.state.task', {});
  const defaults = get(p, 'location.state.defaults', {});
  const leaveLogicPlanId = get(p, 'location.state.leaveLogicPlanId');

  return { lastPath, task, defaults, leaveLogicPlanId };
};

export const isStepHidden = (
  submitEvent: FormSubmitClickEvent,
  formStep: models.FormStep,
  formContext: IFormContext,
) => {
  // only check further if the "Hide When No Fields" flag is set on the step
  if (
    formStep.elements.flags?.value.some(
      (f) => f.codename === enums.FormStepFlags.hideWhenNoFields,
    )
  ) {
    // check step sections
    return !formStep.elements.sections?.linkedItems?.some((s) => {
      // only if they are fields
      if (s.system.type !== enums.FormSectionTypes.FormField) {
        return false;
      }

      // options template
      const ot = parseJSONOrDefault(
        (s as any).elements?.options_template?.value,
      );

      return options.shouldShow(
        ot,
        (key) => submitEvent.values[key],
        formContext,
      );
    });
  } else if (formStep.elements.options_template?.value) {
    // options template
    const ot = parseJSONOrDefault(formStep.elements.options_template?.value);

    return !options.shouldShow(
      ot,
      (key) => submitEvent.values[key],
      formContext,
    );
  }

  return false;
};

// remove non numeric chars from mobile number
export const formatPhoneNumber = (phone: string | undefined) => {
  if (!phone) return phone;
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)})-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return phone;
};

export const handleSmsNotification = async (
  eventValues: any,
  formContext: IFormContext,
  setFormContext: Function,
  communicationPreferences: any,
  communicationPreferencesData: Function,
) => {
  if (communicationPreferences && !formContext?.currentSmsNumber) {
    communicationPreferencesData(setFormContext);
  }
  let notificationsNumber = '';
  if (eventValues.smsNotificationSelection === 'Yes') {
    if (formContext?.currentSmsNumber && !eventValues.smsPhoneNumber) {
      notificationsNumber = formContext.currentSmsNumber;
    } else if (eventValues.smsPhoneNumber) {
      notificationsNumber = eventValues.smsPhoneNumber.replaceAll(/\D/g, '');
    }
    setFormContext((fc: IFormContext) => ({
      ...fc,
      updatedSmsPref: 'Opt-In',
      updatedNumber: notificationsNumber,
    }));
    updateComPrefs(
      notificationsNumber,
      setFormContext,
      communicationPreferences,
    );
    await updateComPrefs(
      notificationsNumber,
      setFormContext,
      communicationPreferences,
    );
  }
};

export const updateComPrefs = async (
  notificationsNumber: string,
  setFormContext: Function,
  communicationPreferences: any,
) => {
  const commsPrefs = communicationPreferences;
  if (commsPrefs) {
    const textingPref = commsPrefs.communicationPreferences?.find(
      (c: any) => c.preferenceType === 'Texting',
    );
    if (textingPref) {
      textingPref.preferenceValue = 'Opt-In';
      if (textingPref.phoneNumber) {
        textingPref.phoneNumber.telephoneNo = notificationsNumber;
        setFormContext((fc: IFormContext) => ({
          ...fc,
          updateSmsNotifications: true,
          communicationPreferences: commsPrefs,
        }));
      }
    } else {
      commsPrefs.communicationPreferences?.push({
        preferenceType: 'Texting',
        preferenceValue: 'Opt-In',
        phoneNumber: {
          phoneType: 'Cell',
          telephoneNo: notificationsNumber,
        },
      });
      setFormContext((fc: IFormContext) => ({
        ...fc,
        updateSmsNotifications: true,
        communicationPreferences: commsPrefs,
      }));
    }
  }
};

/**
 * Resolves a Kontent FormStep codename to its zero-based index in the
 * provided formSteps array. Returns -1 when the codename is not found.
 */
export const resolveStepIndexByCodename = (
  formSteps: models.FormStep[],
  codename: string,
): number => formSteps.findIndex((s) => s.system.codename === codename);
